/**
 * Payouts, ledger, bank accounts, advances (uc-core src/modules/payout/controllers):
 *   GET  /payouts ?day&sessionId&status&holdReason&kind&farmerId&unitId&page&limit
 *   GET  /payouts/:id                          row + blockers[] + tries[]
 *   POST /payouts/:id/approve                  409 { blockers } · 403 own work (two-person, R11)
 *   POST /payouts/:id/cancel { reason }
 *   POST /payouts/:id/pay-again { route, utr?, paidOn?, proofMediaId? }
 *   POST /payouts/:id/manual { utr, paidOn, proofMediaId? } · /payouts/:id/manual/confirm
 *   POST /payouts/:id/mark-failed { note }
 *   POST /payout-bulk-files { payoutIds[] } → { bulkFileRef, csv, accepted[], refused[] }
 *   POST /payout-bulk-files/:ref/uploaded · /payout-bulk-files/:ref/results { csv }
 *   GET  /farmers/:id/ledger ?unitId&kind · /farmers/:id/balance · /farmers/:id/bank-accounts
 *   POST /farmers/:id/bank-accounts/verify { accountNumber, ifsc, nameOnPassbook?, consent: true }
 *   GET  /farmers/:id/bank-accounts/verifications/:validationId
 *   POST /farmers/:id/bank-accounts/:accountId/approve
 *   POST /ledger/advances/import { batchRef, financeSignedOff, financeSignOffById, entries[] }
 * Money comes as ₹ decimal strings; it is kept as integer paise here.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getList, getOne, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { arr, bool, inrToPaise0, iso, num, obj, str, strOrNull } from './mappers'
import type { LedgerLine } from './gate'
import { LIVE_REFRESH_MS } from './openDeliveries'

export const PAYOUT_STATUSES = [
  'PENDING_APPROVAL',
  'READY',
  'SUBMITTING',
  'PROCESSING',
  'PROCESSED',
  'FAILED',
  'REVERSED',
  'CANCELLED',
] as const

/** The spec's words for payout statuses (F4.9 screen). */
export const PAYOUT_STATUS_LABEL: Record<string, string> = {
  PENDING_APPROVAL: 'Waiting for approval',
  READY: 'Ready to send',
  SUBMITTING: 'Sending',
  PROCESSING: 'With the bank',
  PROCESSED: 'Paid',
  FAILED: 'Failed',
  REVERSED: 'Returned',
  CANCELLED: 'Cancelled',
}

export const BLOCKER_LABEL: Record<string, string> = {
  TAG_CONFLICT: 'Same tag on two bags',
  OPEN_DISPUTE: 'Open dispute',
  AMOUNT_MISMATCH: 'Phone and server figures do not match',
  BAG_COUNT_MISMATCH: 'Bag count does not match',
  DEVICE_REVOKED: 'Recorded on a switched-off phone',
  PAPER_DAY_NOT_REVIEWED: 'Paper day not reviewed',
  PAPER_DAY: 'Paper day not re-weighed at the warehouse',
  PAGE_PHOTO_MISSING: 'No photo of the paper slip',
  PAGE_PHOTO_NOT_ARRIVED: 'Slip photo not uploaded yet',
  NO_VERIFIED_ACCOUNT: 'No verified bank account',
}

/**
 * Why a payment waits for a person (payouts.hold_reason), in words. V1 holds pay only when
 * ownership or the amount is in doubt, or for a paper day; quality flags never hold pay.
 */
export const PAY_HOLD_LABEL: Record<string, string> = {
  TOP_UP: 'Top-up after a correction',
  LOT_ON_HOLD: 'Lot on hold: ownership in doubt',
  TAG_CONFLICT: 'Same tag on two bags',
  OPEN_DISPUTE: 'Open dispute',
  AMOUNT_MISMATCH: 'Phone and server amounts differ',
  PAPER_DAY: 'Paper day: check the slip photo, then approve',
  TYPED_SACK: 'Sack weight typed at the gate: check it, then approve',
  RATE_CARD: 'Rate card not in force',
  NO_VERIFIED_ACCOUNT: 'No verified bank account',
  OVER_AUTO_LIMIT: 'Above the automatic payment limit',
  OVER_IMPS_LIMIT: 'Above the IMPS limit: goes by NEFT',
  AUTO_PAYOUT_OFF: 'Automatic payments are switched off',
  BAG_COUNT_MISMATCH: 'Bag count differs',
  OPERATOR_WEIGHT_BIAS: 'Operator weighs differently',
  SESSION_BLOCKED: 'Collection day blocked',
  SCALE_TEST: 'Scale test failed',
}

export function payHoldLabel(code: string | null | undefined): string {
  if (!code) return ''
  return (
    PAY_HOLD_LABEL[code] ??
    code
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/^./, (c) => c.toUpperCase())
  )
}

export interface Blocker {
  code: string
  detail: Record<string, unknown>
}

export interface PayoutRow {
  id: string
  unitId: string
  unitCode: string
  stream: string
  kind: string
  farmerId: string | null
  farmerName: string | null
  villageName: string | null
  sessionId: string | null
  collectionDay: string | null
  lotValuePaise: number
  advanceRecoveredPaise: number
  overpaymentRecoveredPaise: number
  amountPaise: number
  mode: string | null
  status: string
  holdReason: string | null
  idempotencyKey: string | null
  attemptNo: number
  retryOfPayoutId: string | null
  utr: string | null
  failureReason: string | null
  requestedAt: string | null
  processedAt: string | null
  bulkFileRef: string | null
  approvedById: string | null
  requestedById: string | null
  manualUtrEnteredById: string | null
  /** Who recorded the manual bank transfer, for the second person confirming it. */
  manualUtrEnteredByName: string | null
  /** Paper day: media id of the slip page photo. */
  slipPhotoId: string | null
  accountId: string | null
  accountLast4: string | null
  accountIfsc: string | null
  accountStatus: string | null
  /** PAPER for a paper-day lot (uc-core sends it from the collection session). */
  captureMode: string | null
  blockers: Blocker[]
}

export function mapPayout(r: Record<string, unknown>): PayoutRow {
  return {
    id: str(r.id),
    unitId: str(r.unitId),
    unitCode: str(r.unitCode),
    stream: str(r.stream, 'MAIN'),
    kind: str(r.kind, 'LOT'),
    farmerId: strOrNull(r.farmerId),
    farmerName: strOrNull(r.farmerName),
    villageName: strOrNull(r.villageName),
    sessionId: strOrNull(r.sessionId),
    collectionDay: strOrNull(r.collectionDay),
    lotValuePaise: inrToPaise0(r.lotValueInr as string),
    advanceRecoveredPaise: inrToPaise0(r.advanceRecoveredInr as string),
    overpaymentRecoveredPaise: inrToPaise0(r.overpaymentRecoveredInr as string),
    amountPaise: inrToPaise0(r.amountInr as string),
    mode: strOrNull(r.mode),
    status: str(r.status),
    holdReason: strOrNull(r.holdReason),
    idempotencyKey: strOrNull(r.idempotencyKey),
    attemptNo: num(r.attemptNo, 1),
    retryOfPayoutId: strOrNull(r.retryOfPayoutId),
    utr: strOrNull(r.utr),
    failureReason: strOrNull(r.failureReason),
    requestedAt: iso(r.requestedAt),
    processedAt: iso(r.processedAt),
    bulkFileRef: strOrNull(r.bulkFileRef),
    approvedById: strOrNull(r.approvedById),
    requestedById: strOrNull(r.requestedById),
    manualUtrEnteredById: strOrNull(r.manualUtrEnteredById),
    manualUtrEnteredByName: strOrNull(r.manualUtrEnteredByName),
    slipPhotoId: strOrNull(r.slipPhotoId),
    accountId: strOrNull(r.accountId),
    accountLast4: strOrNull(r.accountLast4),
    accountIfsc: strOrNull(r.accountIfsc),
    accountStatus: strOrNull(r.accountStatus),
    captureMode: strOrNull(r.captureMode),
    blockers: arr(r.blockers).map((b) => ({ code: str(b.code), detail: obj(b.detail) })),
  }
}

export interface PayoutTry {
  id: string
  attemptNo: number
  idempotencyKey: string | null
  status: string
  mode: string | null
  amountPaise: number
  utr: string | null
  failureReason: string | null
  requestedAt: string | null
  processedAt: string | null
  bulkFileRef: string | null
}
export interface PayoutDetail extends PayoutRow {
  tries: PayoutTry[]
}
export const mapPayoutDetail = (r: Record<string, unknown>): PayoutDetail => ({
  ...mapPayout(r),
  tries: arr(r.tries).map((t) => ({
    id: str(t.id),
    attemptNo: num(t.attemptNo, 1),
    idempotencyKey: strOrNull(t.idempotencyKey),
    status: str(t.status),
    mode: strOrNull(t.mode),
    amountPaise: inrToPaise0(t.amountInr as string),
    utr: strOrNull(t.utr),
    failureReason: strOrNull(t.failureReason),
    requestedAt: iso(t.requestedAt),
    processedAt: iso(t.processedAt),
    bulkFileRef: strOrNull(t.bulkFileRef),
  })),
})

export interface PayoutFilters extends PageParams {
  day?: string
  sessionId?: string
  status?: string
  holdReason?: string
  kind?: 'LOT' | 'TOP_UP' | 'ALL'
  farmerId?: string
  unitId?: string
}

export const fetchPayouts = (f: PayoutFilters) => getPage('/payouts', f, mapPayout)

export function usePayouts(f: PayoutFilters, enabled = true) {
  return usePageQuery([...ck.payouts, 'list', f], () => fetchPayouts(f), {
    enabled,
    refetchInterval: LIVE_REFRESH_MS,
  })
}

export function usePayout(id: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.payouts, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/payouts/${id}`, mapPayoutDetail),
  })
}

/** Hours since a failed try failed (the F4-B4.a clock; red after 24 h). */
export function failureClockHours(
  p: Pick<PayoutRow, 'processedAt' | 'requestedAt'>,
  now = Date.now(),
): number | null {
  const at = p.processedAt ?? p.requestedAt
  if (!at) return null
  return Math.max(0, Math.floor((now - new Date(at).getTime()) / 3_600_000))
}

/**
 * Two-person rule on the client — the server is the authority; this only explains why the
 * Approve / Confirm buttons are off for the person who did the work.
 */
export function ownWorkReason(
  p: Pick<PayoutRow, 'requestedById' | 'manualUtrEnteredById'>,
  userId: string | undefined,
  action: 'approve' | 'manual-confirm',
): string | null {
  if (!userId) return null
  if (action === 'manual-confirm' && p.manualUtrEnteredById === userId)
    return 'You recorded this transfer: a second person must confirm it.'
  if (action === 'approve' && p.requestedById === userId)
    return 'You raised this payment (e.g. by a correction): a second person must approve it.'
  return null
}

const TOUCHED = [ck.payouts, ck.lots, ck.farmers, ck.dashboard, ck.exceptions]

export function useApprovePayout(onError?: (e: unknown) => boolean | void) {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.post(`/payouts/${id}/approve`),
    invalidate: TOUCHED,
    success: 'Approved — ready to send',
    errorTitle: 'Not approved',
    onError,
  })
}
export function useCancelPayout() {
  return useConsoleMutation<{ id: string; reason: string }>({
    mutationFn: ({ id, reason }) => api.post(`/payouts/${id}/cancel`, { reason }),
    invalidate: TOUCHED,
    success: 'Payout cancelled',
    errorTitle: 'Not cancelled',
  })
}
export type PayAgainRoute = 'SAME_ACCOUNT' | 'NEW_ACCOUNT' | 'BULK_FILE' | 'MANUAL'
export const PAY_AGAIN_ROUTES: Array<{ value: PayAgainRoute; label: string; hint: string }> = [
  {
    value: 'SAME_ACCOUNT',
    label: 'Same account',
    hint: 'Worth trying again: the bank reason was not about the account.',
  },
  {
    value: 'NEW_ACCOUNT',
    label: 'New checked account',
    hint: 'Uses his newly verified primary account.',
  },
  {
    value: 'BULK_FILE',
    label: 'Bulk file',
    hint: 'Goes out in the next bulk upload file. Not used in V1.',
  },
  {
    value: 'MANUAL',
    label: 'Manual transfer',
    hint: 'Paid from the bank portal; a second person confirms the UTR.',
  },
]
export function usePayAgain() {
  return useConsoleMutation<{
    id: string
    route: PayAgainRoute
    utr?: string
    paidOn?: string
    proofMediaId?: string
  }>({
    mutationFn: ({ id, ...body }) => api.post(`/payouts/${id}/pay-again`, body),
    invalidate: TOUCHED,
    success: 'New try raised',
    errorTitle: 'Could not pay again',
  })
}
export function useManualTransfer() {
  return useConsoleMutation<{ id: string; utr: string; paidOn: string; proofMediaId?: string }>({
    mutationFn: ({ id, ...body }) => api.post(`/payouts/${id}/manual`, body),
    invalidate: TOUCHED,
    success: 'Bank transfer recorded — waiting for a second person to confirm',
    errorTitle: 'Transfer not recorded',
  })
}
export function useConfirmManual() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.post(`/payouts/${id}/manual/confirm`),
    invalidate: TOUCHED,
    success: 'Transfer confirmed — marked paid',
    errorTitle: 'Not confirmed',
  })
}
export function useMarkFailed() {
  return useConsoleMutation<{ id: string; note: string }>({
    mutationFn: ({ id, note }) => api.post(`/payouts/${id}/mark-failed`, { note }),
    invalidate: TOUCHED,
    success: 'Marked failed',
    errorTitle: 'Not marked failed',
  })
}

export interface BulkFile {
  bulkFileRef: string
  csv: string
  accepted: string[]
  refused: Array<{ payoutId: string; reason: string }>
}
export function useCreateBulkFile() {
  return useConsoleMutation<string[], BulkFile>({
    mutationFn: async (payoutIds) => {
      const r = obj(await api.post('/payout-bulk-files', { payoutIds }))
      return {
        bulkFileRef: str(r.bulkFileRef),
        csv: str(r.csv),
        accepted: arr<string>(r.accepted).map(String),
        refused: arr(r.refused).map((x) =>
          typeof x === 'string'
            ? { payoutId: x, reason: '' }
            : { payoutId: str(x.payoutId ?? x.id), reason: str(x.reason ?? x.code) },
        ),
      }
    },
    invalidate: TOUCHED,
    success: (r) => `Bulk file ${r.bulkFileRef}: ${r.accepted.length} payments`,
    errorTitle: 'Bulk file not made',
  })
}
export function useMarkBulkUploaded() {
  return useConsoleMutation<string>({
    mutationFn: (ref) => api.post(`/payout-bulk-files/${encodeURIComponent(ref)}/uploaded`),
    invalidate: TOUCHED,
    success: 'Marked uploaded',
  })
}
export interface BulkResults {
  rows: number
  results: Array<{ referenceId?: string; result: string }>
}
export function useImportBulkResults() {
  return useConsoleMutation<{ ref: string; csv: string }, BulkResults>({
    mutationFn: async ({ ref, csv }) => {
      const r = obj(
        await api.post(`/payout-bulk-files/${encodeURIComponent(ref)}/results`, { csv }),
      )
      return {
        rows: num(r.rows),
        results: arr(r.results).map((x) => ({
          referenceId: strOrNull(x.referenceId) ?? undefined,
          result: str(x.result),
        })),
      }
    },
    invalidate: TOUCHED,
    success: (r) => `${r.rows} result rows applied`,
    errorTitle: 'Results not imported',
  })
}

// ============================== farmers ==============================

export interface FarmerBalance {
  farmerId: string
  owedOnLotsPaise: number
  advanceOutstandingPaise: number
  netPositionPaise: number
  lastEntryAt: string | null
  openLots: Array<{ unitId: string; unitCode: string; balancePaise: number }>
}
export function useFarmerBalance(farmerId: string | undefined) {
  return useQuery({
    queryKey: [...ck.farmers, farmerId, 'balance'],
    enabled: Boolean(farmerId),
    queryFn: () =>
      getOne(`/farmers/${farmerId}/balance`, (r): FarmerBalance => ({
        farmerId: str(r.farmerId),
        owedOnLotsPaise: inrToPaise0(r.owedOnLotsInr as string),
        advanceOutstandingPaise: inrToPaise0(r.advanceOutstandingInr as string),
        netPositionPaise: inrToPaise0(r.netPositionInr as string),
        lastEntryAt: iso(r.lastEntryAt),
        openLots: arr(r.openLots).map((l) => ({
          unitId: str(l.unitId),
          unitCode: str(l.unitCode),
          balancePaise: inrToPaise0(l.balanceInr as string),
        })),
      })),
  })
}

export const mapLedger = (r: Record<string, unknown>): LedgerLine => ({
  id: str(r.id),
  kind: str(r.kind),
  amountPaise: inrToPaise0((r.amountInr ?? r.amount_inr) as string),
  unitId: strOrNull(r.unitId),
  unitCode: strOrNull(r.unitCode),
  payoutId: strOrNull(r.payoutId),
  occurredAt: iso(r.occurredAt),
  correctsEntryId: strOrNull(r.correctsEntryId),
  note: strOrNull(r.note),
})
export function useFarmerLedger(
  farmerId: string | undefined,
  f: PageParams & { unitId?: string; kind?: string },
) {
  return usePageQuery(
    [...ck.farmers, farmerId, 'ledger', f],
    () => getPage(`/farmers/${farmerId}/ledger`, f, mapLedger),
    { enabled: Boolean(farmerId) },
  )
}

export interface BankAccount {
  id: string
  accountLast4: string | null
  ifsc: string | null
  bankName: string | null
  nameAtBank: string | null
  provider: string | null
  verificationMethod: string | null
  verifiedAt: string | null
  status: string
  isPrimary: boolean
  hasFundAccount: boolean
  addedById: string | null
  approvedById: string | null
  approvedAt: string | null
  disabledAt: string | null
  disabledReason: string | null
  createdAt: string | null
}
export const mapBankAccount = (r: Record<string, unknown>): BankAccount => ({
  id: str(r.id),
  accountLast4: strOrNull(r.accountLast4),
  ifsc: strOrNull(r.ifsc),
  bankName: strOrNull(r.bankName),
  nameAtBank: strOrNull(r.nameAtBank),
  provider: strOrNull(r.provider),
  verificationMethod: strOrNull(r.verificationMethod),
  verifiedAt: iso(r.verifiedAt),
  status: str(r.status),
  isPrimary: bool(r.isPrimary),
  hasFundAccount: bool(r.hasFundAccount),
  addedById: strOrNull(r.addedById),
  approvedById: strOrNull(r.approvedById),
  approvedAt: iso(r.approvedAt),
  disabledAt: iso(r.disabledAt),
  disabledReason: strOrNull(r.disabledReason),
  createdAt: iso(r.createdAt),
})
export function useBankAccounts(farmerId: string | undefined) {
  return useQuery({
    queryKey: [...ck.farmers, farmerId, 'bank-accounts'],
    enabled: Boolean(farmerId),
    queryFn: () => getList(`/farmers/${farmerId}/bank-accounts`, undefined, mapBankAccount),
  })
}

/** "XXXX 1234" — never more than the last four digits. */
export function maskAccount(last4: string | null | undefined): string {
  if (!last4) return '—'
  return `•••• ${String(last4).slice(-4)}`
}

export interface Verification {
  validationId: string
  status: string
  accountId?: string | null
  provider?: string | null
  accountStatus?: string | null
  accountLast4?: string | null
  ifsc?: string | null
  nameAtBank?: string | null
  bankName?: string | null
  branch?: string | null
  city?: string | null
}
export function useStartVerification(farmerId: string) {
  return useConsoleMutation<
    {
      accountNumber: string
      ifsc: string
      nameOnPassbook?: string
      consent: true
    },
    Verification
  >({
    mutationFn: async (body) => {
      const r = obj(await api.post(`/farmers/${farmerId}/bank-accounts/verify`, body))
      const verification = obj(r.verification)
      return {
        validationId: str(r.validationId),
        status: str(r.status),
        accountId: strOrNull(r.accountId),
        provider: strOrNull(verification.provider),
        accountStatus: strOrNull(verification.accountStatus),
        accountLast4: strOrNull(verification.accountLast4),
        ifsc: strOrNull(verification.ifsc),
        nameAtBank: strOrNull(verification.nameAtBank),
        bankName: strOrNull(verification.bankName),
        branch: strOrNull(verification.branch),
        city: strOrNull(verification.city),
      }
    },
    invalidate: [ck.farmers],
    success: 'Bank account verified',
    errorTitle: 'Check not started',
  })
}
export function useApproveBankAccount(farmerId: string) {
  return useConsoleMutation<string>({
    mutationFn: (accountId) => api.post(`/farmers/${farmerId}/bank-accounts/${accountId}/approve`),
    invalidate: [ck.farmers, ck.payouts],
    success: 'Account approved as primary',
    errorTitle: 'Not approved',
  })
}

export interface AdvanceEntry {
  farmerId: string
  amountInr: string
  occurredAt: string
  externalRef: string
}
export interface ImportResult {
  imported: number
  skipped: Array<Record<string, unknown>>
}
export function useImportAdvances() {
  return useConsoleMutation<
    {
      batchRef: string
      financeSignedOff: true
      financeSignOffById: string
      entries: AdvanceEntry[]
    },
    ImportResult
  >({
    mutationFn: async (body) => {
      const r = obj(await api.post('/ledger/advances/import', body))
      return { imported: num(r.imported), skipped: arr(r.skipped) }
    },
    invalidate: [ck.farmers],
    success: (r) => `${r.imported} advances imported`,
    errorTitle: 'Import failed',
  })
}
